import arrowUpIcon from "@assets/icons/arrow_up.svg";
import stopIcon from "@assets/icons/stop.svg";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { Box, IconButton, InputBase, Tooltip, useTheme } from "@mui/material";
import MicRecorder from "mic-recorder-to-mp3";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const RECORDING_TIMEOUT_MS = 15000; // 15 segundos

// Detectar si estamos en iOS dentro de un WebView (app nativa)
const isIOSWebView = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return false;

  const ua = navigator.userAgent || "";
  const isIOSDevice = /iP(hone|od|ad)/.test(ua);
  const hasWebkitHandler = !!(window as any).webkit?.messageHandlers;

  return isIOSDevice && hasWebkitHandler;
};

// Detectar el mejor tipo MIME de audio soportado para iOS
const getBestAudioMimeType = (): string | undefined => {
  if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) {
    return undefined;
  }

  // Orden de preferencia para iOS
  const mimeTypes = [
    "audio/mp4",
    "audio/aac",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/wav"
  ];

  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return undefined;
};

// Standardized props interface
type InteractiveChatTextInputProps = {
  // Standard text input props
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onRecord: (blob: Blob) => void;
  audioBlob?: Blob | null;
  onDeleteAudio?: () => void;
  onStop?: () => void;
  // UI props
  label: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
};

export const InteractiveChatTextInput = (
  props: InteractiveChatTextInputProps
) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isRecording, setIsRecording] = useState(false);

  // Modo MicRecorder (actual web)
  const recorderRef = useRef<InstanceType<typeof MicRecorder> | null>(null);
  // Modo MediaRecorder nativo (iOS WebView)
  const useNativeRecorderRef = useRef<boolean>(false);
  const nativeMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const nativeStreamRef = useRef<MediaStream | null>(null);
  const nativeChunksRef = useRef<BlobPart[]>([]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isStartingRef = useRef<boolean>(false);

  const {
    value,
    onChange,
    onSubmit,
    onRecord,
    audioBlob,
    onDeleteAudio = () => {},
    onStop = () => {},
    label,
    placeholder,
    disabled = false,
    loading = false
  } = props;

  const handleTextSubmit = () => {
    if (value.trim() === "" && !audioBlob) return;
    onSubmit();
  };

  const stopNativeRecording = () => {
    // Limpiar timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const mediaRecorder = nativeMediaRecorderRef.current;
    if (!mediaRecorder) {
      setIsRecording(false);
      return;
    }

    try {
      if (
        mediaRecorder.state === "recording" ||
        mediaRecorder.state === "paused"
      ) {
        // Solicitar datos finales antes de detener
        if (mediaRecorder.state === "recording") {
          mediaRecorder.requestData();
        }

        // Pequeño delay para asegurar que se procesen los datos pendientes
        setTimeout(() => {
          mediaRecorder.stop();
        }, 50);
      } else {
        setIsRecording(false);
      }
    } catch (error) {
      console.error("Error al detener MediaRecorder nativo:", error);
      setIsRecording(false);
    }
  };

  const stopRecording = async () => {
    // Clear timeout first
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Rama nativa (MediaRecorder) para iOS WebView
    if (useNativeRecorderRef.current) {
      if (!isRecording || !nativeMediaRecorderRef.current) {
        setIsRecording(false);
        return;
      }
      stopNativeRecording();
      return;
    }

    // Early return if not recording or no recorder
    if (!isRecording || !recorderRef.current) {
      setIsRecording(false);
      return;
    }

    // Prevent concurrent stop calls
    if (isStartingRef.current) {
      return;
    }

    const recorder = recorderRef.current;
    // Clear the ref immediately to prevent double-stops
    recorderRef.current = null;

    try {
      const [, blob] = await recorder.stop().getMp3();

      // Validate the blob before using it
      if (!blob) {
        console.error("No blob returned from recorder");
        setIsRecording(false);
        return;
      }

      if (blob.size === 0) {
        console.error("Audio blob is empty (size: 0)");
        setIsRecording(false);
        return;
      }

      // Only call onRecord if we have a valid blob
      onRecord(blob);
    } catch (error) {
      console.error("Error stopping recording:", error);
      // Don't call onRecord if there's an error
    } finally {
      setIsRecording(false);
    }
  };

  const startNativeRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error("getUserMedia no disponible en este entorno");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      nativeStreamRef.current = stream;
      nativeChunksRef.current = [];

      const mimeType = getBestAudioMimeType() || "audio/mp4";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      nativeMediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          nativeChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        try {
          const blob = new Blob(nativeChunksRef.current, { type: mimeType });

          // Limpiar refs
          nativeChunksRef.current = [];
          nativeMediaRecorderRef.current = null;
          if (nativeStreamRef.current) {
            nativeStreamRef.current.getTracks().forEach((t) => t.stop());
            nativeStreamRef.current = null;
          }

          if (blob.size === 0) {
            console.error("Audio blob nativo vacío (size: 0)");
            setIsRecording(false);
            return;
          }

          onRecord(blob);
        } catch (err) {
          console.error("Error en onstop de MediaRecorder nativo:", err);
        } finally {
          setIsRecording(false);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("Error en MediaRecorder nativo:", event);
        setIsRecording(false);
      };

      // Usar timeslice de 100ms para recibir datos periódicamente
      mediaRecorder.start(100);

      setIsRecording(true);

      timeoutRef.current = setTimeout(() => {
        stopNativeRecording();
      }, RECORDING_TIMEOUT_MS);
    } catch (error) {
      console.error("Error iniciando grabación nativa:", error);
      setIsRecording(false);
      nativeMediaRecorderRef.current = null;
      if (nativeStreamRef.current) {
        nativeStreamRef.current.getTracks().forEach((t) => t.stop());
        nativeStreamRef.current = null;
      }
    }
  };

  const startRecording = async () => {
    if (isRecording || isStartingRef.current) return;
    if (audioBlob) onDeleteAudio();

    // Detectar si debemos usar el MediaRecorder nativo (iOS WebView)
    const shouldUseNative =
      isIOSWebView() &&
      typeof window !== "undefined" &&
      (window as any).MediaRecorder &&
      typeof MediaRecorder.isTypeSupported === "function" &&
      MediaRecorder.isTypeSupported("audio/mp4");

    useNativeRecorderRef.current = shouldUseNative;

    if (shouldUseNative) {
      await startNativeRecording();
      return;
    }

    // Ensure any existing recorder is cleaned up
    // (should be null already from stopRecording, but double-check)
    if (recorderRef.current) {
      try {
        // Try to stop and clean up any existing recorder
        const oldRecorder = recorderRef.current;
        recorderRef.current = null;
        await oldRecorder
          .stop()
          .getMp3()
          .catch(() => {
            // Ignore errors when cleaning up old recorder
          });
      } catch {
        // Ignore cleanup errors
      }
      // Small delay to ensure audio context is fully released
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Create a fresh recorder instance for each recording
    const bestMimeType = getBestAudioMimeType();
    const recorderOptions: { bitRate: number; mimeType?: string } = {
      bitRate: 128
    };

    if (bestMimeType) {
      recorderOptions.mimeType = bestMimeType;
    }

    const newRecorder = new MicRecorder(recorderOptions);
    recorderRef.current = newRecorder;

    try {
      isStartingRef.current = true;
      await newRecorder.start();

      setIsRecording(true);

      timeoutRef.current = setTimeout(() => {
        stopRecording();
      }, RECORDING_TIMEOUT_MS);
    } catch (error) {
      console.error("Error starting recording:", error);
      setIsRecording(false);
      recorderRef.current = null;
    } finally {
      isStartingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Limpiar MediaRecorder nativo si sigue activo
      if (nativeMediaRecorderRef.current) {
        try {
          if (
            nativeMediaRecorderRef.current.state === "recording" ||
            nativeMediaRecorderRef.current.state === "paused"
          ) {
            nativeMediaRecorderRef.current.stop();
          }
        } catch {
          // Ignorar errores de cleanup
        }
        nativeMediaRecorderRef.current = null;
      }
      if (nativeStreamRef.current) {
        nativeStreamRef.current.getTracks().forEach((t) => t.stop());
        nativeStreamRef.current = null;
      }

      // Clean up recorder if it exists
      if (recorderRef.current) {
        try {
          recorderRef.current
            .stop()
            .getMp3()
            .catch(() => {
              // Ignore cleanup errors
            });
        } catch {
          // Ignore cleanup errors
        }
        recorderRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* mic errors are intentionally not shown to avoid confusion in non-HTTPS local testing */}

      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          borderRadius: "66px",
          p: 1,
          height: 54,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.customColors.inputBorder}`,
          boxShadow: theme.palette.customColors.shadowLow
        }}
      >
        <InputBase
          fullWidth
          placeholder={
            loading
              ? t("chatbot.common.responding", "Respondent...")
              : placeholder
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !loading) {
              handleTextSubmit();
            }
          }}
          disabled={disabled || loading || isRecording || Boolean(audioBlob)}
          sx={{
            flex: 1,
            pl: 2,
            pr: 2,
            fontSize: 16,
            color: theme.palette.customColors.textSecondary
          }}
          inputProps={{ "aria-label": label }}
        />

        <Box display="flex" alignItems="center" gap={1}>
          {loading ? (
            <Tooltip title={t("chatbot.actions.stop", "Detener")}>
              <span>
                <IconButton
                  onClick={onStop}
                  sx={{
                    width: 40,
                    height: 40,
                    // Fondo del círculo: chipBg
                    bgcolor: `${theme.palette.customColors.chipBg} !important`,
                    color: theme.palette.customColors.iconInactive,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    minWidth: 40,
                    padding: 0,
                    "&:hover": {
                      bgcolor:
                        theme.palette.customColors.grayLight + " !important"
                    },
                    "&.Mui-disabled": {
                      bgcolor: `${theme.palette.customColors.chipBg} !important`
                    }
                  }}
                >
                  <img
                    src={stopIcon}
                    alt="Stop"
                    style={{ width: 20, height: 20 }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <>
              <Tooltip
                title={
                  isRecording
                    ? t("chatbot.actions.stop_recording", "Detener grabación")
                    : t("chatbot.actions.start_recording")
                }
              >
                <span>
                  <IconButton
                    onClick={() => {
                      if (isRecording) {
                        stopRecording();
                      } else {
                        startRecording();
                      }
                    }}
                    disabled={disabled || loading}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: isRecording
                        ? theme.palette.error.main
                        : theme.palette.customColors.chipBg,
                      color: isRecording
                        ? theme.palette.common.white
                        : theme.palette.customColors.iconInactive,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        backgroundColor: isRecording
                          ? theme.palette.customColors.recordingRed
                          : theme.palette.customColors.grayLight
                      }
                    }}
                  >
                    {isRecording ? (
                      <MicOffIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <MicIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("chatbot.actions.send_message")}>
                <span>
                  <IconButton
                    onClick={handleTextSubmit}
                    disabled={
                      disabled ||
                      loading ||
                      ((value || "").trim() === "" && !audioBlob)
                    }
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor:
                        (value || "").trim() !== "" || audioBlob
                          ? theme.palette.customColors.textPrimary
                          : theme.palette.customColors.chipBg,
                      color:
                        (value || "").trim() !== "" || audioBlob
                          ? theme.palette.common.white
                          : theme.palette.customColors.iconInactive,
                      border: "none",
                      borderRadius: "50%",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor:
                          (value || "").trim() !== "" || audioBlob
                            ? theme.palette.customColors.textSecondary
                            : theme.palette.customColors.inputBorder
                      },
                      "&:disabled": {
                        bgcolor: theme.palette.customColors.chipBg,
                        color: theme.palette.customColors.iconInactive
                      },
                      transition: "all 0.2s ease-in-out"
                    }}
                  >
                    <img
                      src={arrowUpIcon}
                      alt="Enviar"
                      style={{
                        width: 20,
                        height: 20,
                        // Blanco cuando está activo, gris cuando está inactivo
                        filter:
                          (value || "").trim() !== "" || audioBlob
                            ? "brightness(0) invert(1)"
                            : "invert(33%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)"
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
